import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  Mouse,
  AttachMoney,
  CalendarToday,
  LocalOffer,
  Business,
  Category,
  Movie,
  Image,
  Language,
  Devices,
  Star,
  Autorenew,
  Edit,
  Delete,
} from "@mui/icons-material";
import config from "../config";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
const baseUrl = config.baseURL;

const AdDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/ads/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        console.log("add detauls dtaa", data);
        // Parse JSON fields safely
        data.tags =
          typeof data.tags === "string"
            ? JSON.parse(data.tags)
            : data.tags || [];
        data.device_target =
          typeof data.device_target === "string"
            ? JSON.parse(data.device_target)
            : data.device_target || [];
        data.targeting_categories =
          typeof data.targeting_categories === "string"
            ? JSON.parse(data.targeting_categories)
            : data.targeting_categories || [];
        data.targeting_industries =
          typeof data.targeting_industries === "string"
            ? JSON.parse(data.targeting_industries)
            : data.targeting_industries || [];
        data.media_file = data.media_file || [];
        data.media_url =
          typeof data.media_url === "string"
            ? JSON.parse(data.media_url || "[]")
            : data.media_url || [];

        setAd(data);
      } catch (err) {
        console.error("Error fetching ad:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
  }, [id, token]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this ad?")) return;
    try {
      await fetch(`${baseUrl}/api/ads/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate("/dashboard/Advertiser/ads");
    } catch (err) {
      console.error("Error deleting ad:", err);
    }
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );

  if (!ad)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "100vh" }}>
        <Typography variant="h6" color="error">
          Ad not found
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ backgroundColor: "#D1D8E0", minHeight: "100vh", p: 4 }}>
      <Card sx={{ maxWidth: 1100, mx: "auto", borderRadius: 3, boxShadow: 6 }}>
        <CardContent>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", pr: 2, pt: 2 }}
          >
            <IconButton
              onClick={() => navigate(`/dashboard/Advertiser/AdEdit/${id}`)}
              sx={{ color: "#2E4053" }}
            >
              <Edit />
            </IconButton>
            <IconButton color="error" onClick={handleDelete}>
              <Delete />
            </IconButton>
          </Box>
          {/* Title & Description */}
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
            {ad.title}
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            {ad.description}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Media */}
          <Grid container spacing={3}>
            {/* Media */}
            <Grid item xs={12} md={6}>
              {ad.media_url.length > 0 ? (
                <Slider dots arrows infinite={false}>
                  {ad.media_url.map((url, idx) =>
                    url.match(/\.(mp4|webm|ogg)$/i) ? (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Movie sx={{ mr: 1 }} />
                        <video
                          src={url}
                          controls
                          style={{ width: "100%", borderRadius: 8 }}
                        />
                      </Box>
                    ) : (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Image sx={{ mr: 1 }} />
                        <Box
                          component="img"
                          src={url}
                          alt={`media-${idx}`}
                          sx={{ width: "100%", borderRadius: 2 }}
                        />
                      </Box>
                    )
                  )}
                </Slider>
              ) : (
                <Typography>No media available</Typography>
              )}
            </Grid>

            {/* Core Details */}
            <Grid item xs={12} md={6}>
              {[
                {
                  icon: <Visibility />,
                  label: "Impressions",
                  key: "impressions",
                },
                { icon: <Mouse />, label: "Clicks", key: "clicks" },
                { icon: <AttachMoney />, label: "Bid Price", key: "bid_price" },
                {
                  icon: <AttachMoney />,
                  label: "Max Budget",
                  key: "max_budget",
                },
                {
                  icon: <AttachMoney />,
                  label: "Current Spend",
                  key: "current_spend",
                },
                {
                  icon: <CalendarToday />,
                  label: "Start Date",
                  key: "start_date",
                },
                { icon: <CalendarToday />, label: "End Date", key: "end_date" },
              ].map((field, i) => (
                <Typography key={i} sx={{ mb: 1 }}>
                  {field.icon} <strong>{field.label}:</strong> {ad[field.key]}
                </Typography>
              ))}

              <Typography sx={{ mb: 1 }}>
                <LocalOffer /> <strong>Status:</strong> {ad.status}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* CTA, Geo, Device, Action Rate, Quality */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1 }}>
                <LocalOffer /> <strong>CTA:</strong> {ad.call_to_action} (
                {ad.call_to_action_value})
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <Language /> <strong>Geo Target:</strong>{" "}
                {ad.geo_target || "None"}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <Devices /> <strong>Device Target:</strong>{" "}
                {ad.device_target?.length ? ad.device_target.join(", ") : "All"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1 }}>
                <Star /> <strong>Estimated Action Rate:</strong>{" "}
                {ad.estimated_action_rate}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <Star /> <strong>Ad Quality:</strong> {ad.ad_quality}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <Autorenew /> <strong>Retargeting Override:</strong>{" "}
                {ad.retargeting_override ? "Yes" : "No"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Tags, Industries, Categories */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              <LocalOffer /> Tags:
            </Typography>
            {ad.tags?.length ? (
              ad.tags.map((tag, idx) => (
                <Chip key={idx} label={tag} sx={{ mr: 1, mb: 1 }} />
              ))
            ) : (
              <Typography>No tags</Typography>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              <Business /> Industries:
            </Typography>
            {ad.targeting_industries?.length ? (
              ad.targeting_industries.map((ind, idx) => (
                <Chip key={idx} label={ind} sx={{ mr: 1, mb: 1 }} />
              ))
            ) : (
              <Typography>None</Typography>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              <Category /> Categories:
            </Typography>
            {ad.targeting_categories?.length ? (
              ad.targeting_categories.map((cat, idx) => (
                <Chip key={idx} label={cat} sx={{ mr: 1, mb: 1 }} />
              ))
            ) : (
              <Typography>None</Typography>
            )}
          </Box>

          {/* Zones */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Zones:</Typography>
            {Array.isArray(ad.zones) && ad.zones.length > 0 ? (
              ad.zones.map((z, idx) => (
                <Chip key={z.id || idx} label={z.name} sx={{ mr: 1, mb: 1 }} />
              ))
            ) : (
              <Typography>No zones assigned</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdDetails;
